import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { get } from 'lodash'
import { Loader } from '../kit/components/loader'
import ENDPOINT_ROUTES from '../constants/APIs'
import BaseService from '../services/base.service'

interface FeatureResponse {
  name: string
  enabled: boolean
  key: string
  id: number
  type: 'development' | 'product'
}

export interface FeatureEnabledState {
  isInitialized: boolean
  settings: { [key: string]: FeatureResponse } | {}
}

interface FeatureFetchSucceeded {
  type: 'GET_FEATURES_ENABLED_SUCCEEDED'
  payload: FeatureResponse[]
}

interface FeatureFetchFailed {
  type: 'GET_FEATURES_ENABLED_FAILED'
}

interface FeatureReset {
  type: 'RESET_FEATURES'
}

export type FeatureEnabledDispatch = (action: FeatureFetchSucceeded | FeatureFetchFailed | FeatureReset) => void

interface Props {
  children: React.ReactNode
  orgId?: number | string | null
}

const FeatureStateContext = createContext<FeatureEnabledState | undefined>(undefined)
const FeatureDispatchContext = createContext<FeatureEnabledDispatch | undefined>(undefined)

const featureReducer = (
  state: FeatureEnabledState,
  action: FeatureFetchSucceeded | FeatureFetchFailed | FeatureReset,
) => {
  switch (action.type) {
    case 'RESET_FEATURES':
      return { isInitialized: false, settings: {} }
    case 'GET_FEATURES_ENABLED_SUCCEEDED':
      return { settings: action.payload, isInitialized: true }
    case 'GET_FEATURES_ENABLED_FAILED':
      return { settings: {}, isInitialized: false }
    default: {
      throw new Error('Unhandled feature enabled action type')
    }
  }
}

const FeatureEnabledProvider = ({ children, orgId }: Props) => {
  const [state, dispatch] = useReducer(featureReducer, { isInitialized: false, settings: {} })
  const request = useMemo(() => new BaseService(), [])

  useEffect(() => {
    if (!orgId) {
      return
    }

    let cancelled = false
    dispatch({ type: 'RESET_FEATURES' })
    ;(async () => {
      try {
        const features = await request.getJSON(ENDPOINT_ROUTES.Features.getFeaturesEnabled(orgId))
        if (!cancelled) {
          dispatch({
            type: 'GET_FEATURES_ENABLED_SUCCEEDED',
            payload: features,
          })
        }
      } catch (e) {
        if (!cancelled) {
          dispatch({
            type: 'GET_FEATURES_ENABLED_FAILED',
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [orgId, request])

  return (
    <FeatureStateContext.Provider value={state}>
      <FeatureDispatchContext.Provider value={dispatch}>{children}</FeatureDispatchContext.Provider>
    </FeatureStateContext.Provider>
  )
}

const useFeatureEnabledState = () => {
  const context = useContext(FeatureStateContext)

  if (context === undefined) {
    throw new Error('useFeatureEnabledState must be used within a FeatureEnabledProvider')
  }

  return context
}

const useFeatureEnabledDispatch = () => {
  const context = useContext(FeatureDispatchContext)

  if (context === undefined) {
    throw new Error('useFeatureEnabledDispatch must be used within a FeatureEnabledProvider')
  }

  return context
}

const withFeatureEnabled = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const dispatch = useFeatureEnabledDispatch()
    const state = useFeatureEnabledState()

    if (!state.isInitialized) {
      return <Loader />
    }

    return <Component featureEnabledDispatch={dispatch} featureEnabledState={state} {...props} />
  }
}

const getFeatureEnabled = (state: FeatureEnabledState, selector: string): FeatureResponse | null => {
  const { isInitialized, settings } = state
  if (!isInitialized) {
    return null
  }

  const slice = get(settings, selector)

  if (!slice) {
    return null
  }

  return slice.enabled ? slice : null
}

export {
  FeatureEnabledProvider,
  useFeatureEnabledState,
  useFeatureEnabledDispatch,
  withFeatureEnabled,
  getFeatureEnabled,
}
